import { useEffect, useState, type FC } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { formatCurrency } from "@/utils/common";
import { calculateSip } from "../../../shared-utils/sipCalculatorCore.mjs";
import {
  areSipInputsValid,
  parseSipMonthlyInput,
  parseSipRateInput,
  parseSipYearsInput,
  SIP_MONTHLY_MAX,
  SIP_MONTHLY_MIN,
  SIP_RATE_MAX,
  SIP_RATE_MIN,
  SIP_YEARS_MAX,
  SIP_YEARS_MIN,
} from "@/utils/sipCalculatorInput";

/**
 * SIP Calculator component that estimates returns for Systematic Investment Plans.
 * @example
 * SIPCalculator()
 * Component rendering a SIP calculator UI form.
 * @param {none} None - This component does not take any arguments.
 * @returns {JSX.Element} A React component that allows users to calculate SIP returns.
 * @description
 *   - Initializes default state values for monthly investment, expected return, and time period.
 *   - Utilizes the `calculateSIP` function to compute results upon state changes.
 *   - Provides UI sliders and input fields for user interaction to adjust investment parameters.
 */
const SIPCalculator: FC = () => {
  const [monthlyInvestment, setMonthlyInvestment] = useState<number>(5000);
  const [expectedReturn, setExpectedReturn] = useState<number>(12);
  const [timePeriod, setTimePeriod] = useState<number>(10);
  const [results, setResults] = useState({
    totalInvestment: 0,
    estimatedReturns: 0,
    maturityValue: 0,
  });
  const [inputErrors, setInputErrors] = useState({
    monthlyInvestment: null as string | null,
    expectedReturn: null as string | null,
    timePeriod: null as string | null,
  });

  useEffect(() => {
    if (!areSipInputsValid(monthlyInvestment, expectedReturn, timePeriod)) {
      setResults({ totalInvestment: 0, estimatedReturns: 0, maturityValue: 0 });
      return;
    }

    setResults(calculateSip(monthlyInvestment, expectedReturn, timePeriod));
  }, [monthlyInvestment, expectedReturn, timePeriod]);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>SIP Calculator</CardTitle>
        <CardDescription>
          Calculate returns on your Systematic Investment Plan (SIP) for mutual funds
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex justify-between">
            <Label htmlFor="monthly-investment">Monthly Investment (₹)</Label>
            <div className="font-medium">{formatCurrency(monthlyInvestment)}</div>
          </div>
          <Slider
            id="monthly-investment"
            min={SIP_MONTHLY_MIN}
            max={SIP_MONTHLY_MAX}
            step={500}
            value={[monthlyInvestment]}
            onValueChange={(value) => {
              setMonthlyInvestment(value[0]);
              setInputErrors((prev) => ({ ...prev, monthlyInvestment: null }));
            }}
          />
          <div className="flex items-center space-x-2">
            <Input
              type="number"
              value={monthlyInvestment}
              onChange={(e) => {
                const parsed = parseSipMonthlyInput(e.target.value, monthlyInvestment);
                setInputErrors((prev) => ({ ...prev, monthlyInvestment: parsed.error }));
                if (!parsed.error) {
                  setMonthlyInvestment(parsed.value);
                }
              }}
              min={SIP_MONTHLY_MIN}
              max={SIP_MONTHLY_MAX}
              className="w-full"
            />
          </div>
          {inputErrors.monthlyInvestment ? (
            <p className="text-sm text-destructive" role="alert">
              {inputErrors.monthlyInvestment}
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <Label htmlFor="expected-return">Expected Annual Return (%)</Label>
            <div className="font-medium">{expectedReturn}%</div>
          </div>
          <Slider
            id="expected-return"
            min={SIP_RATE_MIN}
            max={SIP_RATE_MAX}
            step={0.5}
            value={[expectedReturn]}
            onValueChange={(value) => {
              setExpectedReturn(value[0]);
              setInputErrors((prev) => ({ ...prev, expectedReturn: null }));
            }}
          />
          <div className="flex items-center space-x-2">
            <Input
              type="number"
              value={expectedReturn}
              onChange={(e) => {
                const parsed = parseSipRateInput(e.target.value, expectedReturn);
                setInputErrors((prev) => ({ ...prev, expectedReturn: parsed.error }));
                if (!parsed.error) {
                  setExpectedReturn(parsed.value);
                }
              }}
              min={SIP_RATE_MIN}
              max={SIP_RATE_MAX}
              className="w-full"
              step="0.5"
            />
          </div>
          {inputErrors.expectedReturn ? (
            <p className="text-sm text-destructive" role="alert">
              {inputErrors.expectedReturn}
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <Label htmlFor="time-period">Time Period (Years)</Label>
            <div className="font-medium">{timePeriod} years</div>
          </div>
          <Slider
            id="time-period"
            min={SIP_YEARS_MIN}
            max={SIP_YEARS_MAX}
            step={1}
            value={[timePeriod]}
            onValueChange={(value) => {
              setTimePeriod(value[0]);
              setInputErrors((prev) => ({ ...prev, timePeriod: null }));
            }}
          />
          <div className="flex items-center space-x-2">
            <Input
              type="number"
              value={timePeriod}
              onChange={(e) => {
                const parsed = parseSipYearsInput(e.target.value, timePeriod);
                setInputErrors((prev) => ({ ...prev, timePeriod: parsed.error }));
                if (!parsed.error) {
                  setTimePeriod(parsed.value);
                }
              }}
              min={SIP_YEARS_MIN}
              max={SIP_YEARS_MAX}
              className="w-full"
            />
          </div>
          {inputErrors.timePeriod ? (
            <p className="text-sm text-destructive" role="alert">
              {inputErrors.timePeriod}
            </p>
          ) : null}
        </div>

        <div className="mt-8 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <div className="text-sm text-gray-500 mb-1">Total Investment</div>
              <div className="text-2xl font-bold text-orange-700">
                {formatCurrency(results.totalInvestment)}
              </div>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <div className="text-sm text-gray-500 mb-1">Estimated Returns</div>
              <div className="text-2xl font-bold text-orange-700">
                {formatCurrency(results.estimatedReturns)}
              </div>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <div className="text-sm text-gray-500 mb-1">Maturity Value</div>
              <div className="text-2xl font-bold text-orange-700">
                {formatCurrency(results.maturityValue)}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between border-t border-gray-200 pt-4">
        <Button
          variant="outline"
          onClick={() => {
            setMonthlyInvestment(5000);
            setExpectedReturn(12);
            setTimePeriod(10);
            setInputErrors({
              monthlyInvestment: null,
              expectedReturn: null,
              timePeriod: null,
            });
          }}
        >
          Reset
        </Button>
        <Button variant="default">Calculate</Button>
      </CardFooter>
    </Card>
  );
};

export default SIPCalculator;
